import { reactive } from "vue";
import { API_BASE_URL, resolveApiRequestUrl } from "./api-url";

// Sesión de /admin, completamente separada de services/auth.js (el
// login de clientes): cookie distinta (thinksign_admin_session, con `path`
// acotado a /admin-console — el navegador ni siquiera la envía a rutas de
// cliente), endpoint distinto, y este estado reactivo propio. Un login no
// contamina al otro aunque compartan dominio/pestaña.
const state = reactive({
  admin: null,
  isAuthenticated: false,
  initialized: false,
});

const request = async (path, options = {}) => {
  const response = await fetch(resolveApiRequestUrl(path, API_BASE_URL), {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const error = new Error(body?.message || `HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  const text = await response.text();
  return text ? JSON.parse(text) : {};
};

export const platformAdminAuth = {
  state,

  isSuperAdmin() {
    return state.admin?.role === "SUPER_ADMIN";
  },

  // SUPER_ADMIN o ADMIN: ambos ven el dashboard (con ingresos) y el
  // catálogo de precios de solo lectura. SUPPORT no — ver
  // backend/src/platform-admin/entities/platform-admin.entity.ts para la
  // matriz completa de permisos por rol.
  isStaff() {
    return (
      state.admin?.role === "SUPER_ADMIN" || state.admin?.role === "ADMIN"
    );
  },

  async forgotPassword(email) {
    await request("/admin-console/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  async resetPassword(token, newPassword) {
    await request("/admin-console/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, newPassword }),
    });
  },

  async login(email, password) {
    const data = await request("/admin-console/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    state.admin = data.admin;
    state.isAuthenticated = true;
    state.initialized = true;
    return data.admin;
  },

  async logout() {
    try {
      await request("/admin-console/auth/logout", { method: "POST" });
    } finally {
      state.admin = null;
      state.isAuthenticated = false;
    }
  },

  // Se llama una vez por carga de página (guard del router): confirma
  // contra el backend si la cookie sigue siendo válida, nunca se asume
  // desde localStorage/sessionStorage.
  async ensureInitialized() {
    if (state.initialized) return state.isAuthenticated;
    try {
      const data = await request("/admin-console/auth/me");
      state.admin = data.admin;
      state.isAuthenticated = true;
    } catch {
      state.admin = null;
      state.isAuthenticated = false;
    } finally {
      state.initialized = true;
    }
    return state.isAuthenticated;
  },

  // Usado por platform-admin-api.js: si una llamada autenticada devuelve
  // 401 a mitad de sesión (cookie vencida), cae la sesión local también.
  handleUnauthorized() {
    state.admin = null;
    state.isAuthenticated = false;
  },
};
